import { useState } from 'react';
import type { ActionPreviewBlock } from '@netsuite-ai/types';
import { useAuthStore } from '../../store/auth.store';
import { resolveBlock } from '../../lib/chat-stream';

interface Props {
  block: ActionPreviewBlock;
}

const FIELD_LABELS: Record<string, string> = {
  to: 'To',
  subject: 'Subject',
  cc: 'CC',
  from: 'From',
};

function formatValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  return Object.entries(value as Record<string, unknown>)
    .map(([k, v]) => `${k}: ${formatValue(v)}`)
    .join(' · ');
}

export function ActionPreview({ block }: Props) {
  // Canonical status from the server: pending | applied | discarded | failed | superseded
  const status = block.status as string;
  const [isEditing, setIsEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const draft = block.draft as Record<string, unknown>;
  const [editDraft, setEditDraft] = useState<Record<string, unknown>>({
    ...draft,
  });
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'support_readonly';

  const HIDDEN = new Set(['body', 'recordType', 'id']);
  const metaFields = Object.entries(editDraft).filter(([k]) => !HIDDEN.has(k));
  const body = formatValue(editDraft.body);
  const dirty = JSON.stringify(editDraft) !== JSON.stringify(draft);

  async function handleApply() {
    setBusy(true);
    await resolveBlock(block.id, 'approve', dirty ? editDraft : undefined);
    setBusy(false);
    setIsEditing(false);
  }

  async function handleDiscard() {
    setBusy(true);
    await resolveBlock(block.id, 'discard');
    setBusy(false);
  }

  function handleEdit() {
    setEditDraft({ ...draft, ...editDraft });
    setIsEditing(true);
  }
  function handleCancel() {
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <div className="my-2 overflow-hidden rounded border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-100 px-4 py-3">
          <p className="font-semibold text-neutral-900">Edit {block.label}</p>
        </div>
        <div className="space-y-3 px-4 py-3">
          {metaFields.map(([key]) => (
            <div key={key} className="flex items-center gap-4 text-sm">
              <span className="w-16 shrink-0 text-neutral-500">
                {FIELD_LABELS[key] ?? key}
              </span>
              <input
                value={formatValue(editDraft[key])}
                onChange={(e) =>
                  setEditDraft((d) => ({ ...d, [key]: e.target.value }))
                }
                className="flex-1 rounded border border-neutral-300 px-3 py-1.5 text-sm text-neutral-900 outline-none focus:border-neutral-500"
              />
            </div>
          ))}
          {body && (
            <p className="line-clamp-4 text-sm text-neutral-500">{body}</p>
          )}
        </div>
        <div className="flex gap-2 border-t border-neutral-100 bg-neutral-100 px-4 py-6">
          <button
            onClick={handleCancel}
            className="bg-danger rounded px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Cancel
          </button>
          <button
            onClick={() => setIsEditing(false)}
            className="bg-ink/14 rounded border border-neutral-300 px-4 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Done editing
          </button>
        </div>
      </div>
    );
  }

  if (status === 'discarded' || status === 'superseded') {
    return (
      <div className="my-2 rounded border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-400">
        {status === 'superseded' ? 'No longer active' : 'Action discarded'}
      </div>
    );
  }

  if (status === 'applied') {
    const result = (
      block as { result?: { tranid?: string; recordId?: string } }
    ).result;
    const ref = result?.tranid ?? result?.recordId;
    return (
      <div className="my-2 rounded border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-500">
        Applied{ref ? ` · ${ref}` : ''}
      </div>
    );
  }

  return (
    <div className="my-2 overflow-hidden rounded border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-100 px-4 py-3">
        <p className="font-semibold text-neutral-900">{block.label}</p>
      </div>

      <div className="px-4 py-3">
        {metaFields.length > 0 && (
          <div className="mb-3 space-y-1">
            {metaFields.map(([key, value]) => (
              <div key={key} className="flex gap-4 text-sm">
                <span className="w-16 shrink-0 text-neutral-500">
                  {FIELD_LABELS[key] ?? key}
                </span>
                <span className="text-neutral-900">{formatValue(value)}</span>
              </div>
            ))}
          </div>
        )}
        {body && (
          <p className="line-clamp-4 text-sm text-neutral-700">{body}</p>
        )}
      </div>

      {status === 'failed' && (
        <p className="border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-600 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
          Couldn’t apply — check the fields and try again.
        </p>
      )}

      {!isReadOnly && (
        <div className="flex gap-2 border-t border-neutral-100 bg-neutral-100 px-4 py-6">
          <button
            onClick={handleDiscard}
            disabled={busy}
            className="bg-danger rounded px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-40"
          >
            Discard
          </button>
          <button
            onClick={handleEdit}
            disabled={busy}
            className="bg-ink/14 rounded border border-neutral-300 px-4 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40"
          >
            Edit
          </button>
          <button
            onClick={handleApply}
            disabled={busy}
            className="bg-ink text-surface-elevated hover:bg-ink/85 active:bg-ink/75 ml-auto rounded px-4 py-1.5 text-sm font-medium disabled:opacity-40"
          >
            {status === 'failed' ? 'Retry' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
}
